import { MmmFeatureNameResponse } from '@blisspointmedia/bpm-types/dist/MMMFeatureNames';
import {
  BrandEquityScenarioPlanningModel,
  GeoMMMScenarioPlanningModel,
  HroasModelStatus,
  HroasPlanNote,
  RMMMScenarioPlanningModel
} from '@blisspointmedia/bpm-types/dist/ScenarioPlanning';
import {
  awaitJSON,
  CometProgress,
  fetchCometSelfInvoke,
  ScenarioPlanningPyLambdaFetch,
  SegmentationMappingLambdaFetch
} from '../../../utils/fetch-utils';

// Mirrors bpm-lambdas' local RMMMScenarioPlanningModelExt: surfaces mmm_job_dates.
// output_file_version, and relaxes rMMM's 4 S3 key fields to optional — a legacy (pre-Meridian)
// run is a different, R-based pipeline that never produces them. hasScenarioArtifacts is the
// authoritative signal for whether they're actually present; false means this run must be
// treated as view-only (never auto-selected, never passed to getModelConfig for its S3 keys).
// Defined locally until published to bpm-types (same convention as KpiType/OutcomeUnit below).
interface ScenarioPlanningModelCommonExt {
  outputFileVersion?: number | null;
}

export type ScenarioPlanningModelExt =
  | (GeoMMMScenarioPlanningModel & ScenarioPlanningModelCommonExt)
  | (Omit<
      RMMMScenarioPlanningModel,
      'fxzModelS3Key' | 'configS3Key' | 'clientDataS3Key' | 'gxWorkspaceS3Key'
    > & {
      fxzModelS3Key?: string;
      configS3Key?: string;
      clientDataS3Key?: string;
      gxWorkspaceS3Key?: string;
      hasScenarioArtifacts: boolean;
    } & ScenarioPlanningModelCommonExt)
  | (BrandEquityScenarioPlanningModel & ScenarioPlanningModelCommonExt);

// Structured, self-describing feature map: `featureIdMap[id]` carries a pre-joined display `name`
// plus a per-dimension `fields` map, and `keys` gives the dimension hierarchy (slug -> order index).
// Consumers read values by dimension and order from `keys` instead of splitting the name string.
// Re-exported from bpm-types for the many local imports that reference the old local name.
export type MmmFeatureNameMap = MmmFeatureNameResponse;

export const getMmmFeatureNames = async (
  company: string,
  modelType: string
): Promise<MmmFeatureNameResponse> => {
  // Served by the segmentationmapping service (moved from scenarioplanning).
  const result = await SegmentationMappingLambdaFetch('/getMmmFeatureNames', {
    method: 'GET',
    params: { company, modelType }
  });
  return awaitJSON<MmmFeatureNameResponse>(result);
};

// Mirrors @blisspointmedia/bpm-types ScenarioPlanning KpiType/OutcomeUnit. Defined locally
// until that package is published; switch to the shared import on the next bpm-types release.
// "revenue" -> outcome is dollars (ROAS); "non_revenue" -> outcome is conversion counts (CPX).
export type KpiType = 'revenue' | 'non_revenue';
export type OutcomeUnit = 'dollars' | 'conversions';

interface BaselineRowBase {
  modelType: 'GeoMMM' | 'BrandEquity' | 'rMMM';
  // ==== HROAS (unmigrated): per-instance id/label so two instances of the same modelType
  // (e.g. two rMMMs) don't collide, and the chart can color/group by model. Absent for the
  // single-model pages. ====
  modelId?: string;
  modelLabel?: string;
  geo: string;
  week: string;
  feature: string;
  // HROAS: canonicalFeatureKey of this row's own `feature`, one key per media feature across models.
  featureKey?: string;
  channel: string;
  platform: string;
  tactic: string;
  // Original channel/platform/tactic before opaque feature ids are replaced with pretty names for
  // display. Present only on transformed rows; the tree's analyze key (refIds) reads these so the
  // model still receives the original feature id. See mmm-feature-mapping + scenarioRedshiftDataToTree.
  rawChannel?: string;
  rawPlatform?: string;
  rawTactic?: string;
}

export interface BaselineSpendRow extends BaselineRowBase {
  directSpend: number;
  // rMMM only
  indirectSpend?: number;
  // rMMM only — amount given away by a demand-capture channel (see reattributedOutRevenue below).
  reattributedOutSpend?: number;
  // rMMM only — directSpend + indirectSpend - reattributedOutSpend, netted server-side.
  actualizedSpend?: number;
  isDemandCapture?: boolean;
}

export interface BaselineRevenueRow extends BaselineRowBase {
  directRevenue: number;
  // rMMM only — posterior credible-interval bounds, not computed for GeoMMM/BrandEquity.
  directRevenueCiLo?: number;
  directRevenueCiHi?: number;
  // Populated generically across model types (nonzero for rMMM/GeoMMM today; zero/undefined for
  // Brand Equity until its two-stage split is populated upstream — PRD G3/§5.3). This is what the
  // HROAS by-model Change Impact Direct/Indirect columns are built from.
  indirectRevenue?: number;
  // rMMM only — posterior credible-interval bounds, not computed for GeoMMM/BrandEquity.
  indirectRevenueCiLo?: number;
  indirectRevenueCiHi?: number;
  // rMMM only — revenue reattributed AWAY from this channel to a demand-generation channel (nonzero
  // only for demand-capture channels). Zero-sum with indirectRevenue across a scenario's channels:
  // actualizedRevenue = directRevenue + indirectRevenue - reattributedOutRevenue.
  reattributedOutRevenue?: number;
  reattributedOutRevenueCiLo?: number;
  reattributedOutRevenueCiHi?: number;
  // rMMM only — directRevenue + indirectRevenue - reattributedOutRevenue, netted server-side.
  actualizedRevenue?: number;
  actualizedRevenueCiLo?: number;
  actualizedRevenueCiHi?: number;
  isDemandCapture?: boolean;
}

export interface LambdaColumn {
  accessorKey: string;
  header: string;
  type: string;
}

export interface LambdaResultSet<T> {
  columns: LambdaColumn[];
  rows: T[];
  totalCount: number;
}

// HROAS only: one entry per model instance the orchestrator skipped (S3/validation failure,
// etc.) — present only when at least one instance failed, so a partial result is distinguishable
// from a full one instead of silently rendering fewer models than expected.
export interface HROASInstanceWarning {
  modelId: string;
  type: HROASModelInstanceType;
  error: string;
}

export interface BaselineData {
  baselineSpend: LambdaResultSet<BaselineSpendRow>;
  baselineRevenue: LambdaResultSet<BaselineRevenueRow>;
  // Surfaced by the scenario-planning lambdas; drives dollars-vs-conversions in the UI.
  kpiType?: KpiType;
  outcomeUnit?: OutcomeUnit;
  warnings?: HROASInstanceWarning[];
}

export interface GeoMMMModelConfig {
  modelType: 'GeoMMM';
  modelS3Key: string;
}

export interface BrandEquityStageOneModelConfig {
  s3Key: string;
  brandMetricName: string;
}

export interface BrandEquityModelConfig {
  modelType: 'BrandEquity';
  stageOneModels: BrandEquityStageOneModelConfig[];
  stageTwoModelS3Key: string;
}

export interface RMMMModelConfig {
  modelType: 'RMMM';
  fxzModelS3Key: string;
  configS3Key: string;
  clientDataS3Key: string;
  gxWorkspaceS3Key: string;
  gxTracesS3Keys: string[];
}

// Matches HROAS.py's model["type"] dispatch — lowercase, NOT ScenarioPlanningModelType's casing.
// Same documented quirk as MODEL_TYPES.R_MMM ('RMMM') vs the lambda's row-level 'rMMM' tag.
export type HROASModelInstanceType = 'geoMMM' | 'brandEquity' | 'rMMM';

// HROAS is run-driven: the backend reads the run's inputs.json (S3 keys never reach the client)
// and builds the model instances for `slotIds` (slot id = `{taxonomy_model_type}:{model_scope}`).
export interface HROASRunArgs {
  cid: string;
  kpi: string;
  runId: string;
  slotIds: string[];
}

export interface HROASModelConfig extends HROASRunArgs {
  modelType: 'HROAS';
}

export type ModelConfig =
  GeoMMMModelConfig | BrandEquityModelConfig | RMMMModelConfig | HROASModelConfig;

// DEV-24669: was a locally-duplicated copy of fetch-utils.ts's CometProgress (same reserved
// {cometProgress: {percent, message, done}} sentinel shape) -- re-exported from there since
// existing callers of this type still reference it from here.
export type { CometProgress };

export interface ScenarioLambdaInput {
  start_date: string;
  end_date: string;
  scenario_spend: Record<string, { spend: number; flighting: string }>;
}

// Analyze result row — shared base with model-specific extensions
export interface AnalyzeResultRow {
  modelType: 'GeoMMM' | 'BrandEquity' | 'rMMM';
  // ==== HROAS (unmigrated): per-instance id/label (see BaselineRowBase) ====
  modelId?: string;
  modelLabel?: string;
  credibleIntervalLevel: number;
  feature: string;
  channel: string;
  platform: string;
  tactic: string;
  directRevenue: number;
  directRevenueCiLo: number;
  directRevenueCiHi: number;
  // rMMM extensions
  directSpend?: number;
  indirectSpend?: number;
  indirectRevenue?: number;
  indirectRevenueCiLo?: number;
  indirectRevenueCiHi?: number;
  // rMMM only — revenue/spend reattributed AWAY from this channel to a demand-generation channel
  // (nonzero only for demand-capture channels). Zero-sum with indirect* across a scenario's
  // channels: actualizedRevenue = directRevenue + indirectRevenue - reattributedOutRevenue.
  reattributedOutSpend?: number;
  reattributedOutRevenue?: number;
  reattributedOutRevenueCiLo?: number;
  reattributedOutRevenueCiHi?: number;
  // rMMM only — direct + indirect - reattributedOut, netted server-side.
  actualizedSpend?: number;
  actualizedRevenue?: number;
  actualizedRevenueCiLo?: number;
  actualizedRevenueCiHi?: number;
  isDemandCapture?: boolean;
  // BrandEquity extensions
  brandEquityMetric?: string;
  stageOneContribution?: number;
  stageOneShare?: number;
  stageTwoContribution?: number;
}

export interface RMMMPairwiseRow {
  modelType: 'rMMM';
  credibleIntervalLevel: string;
  estimateType: 'mean' | 'ci_lo' | 'ci_hi';
  primaryChannel: string;
  primaryPlatform: string;
  primaryTactic: string;
  primaryName: string;
  secondaryChannel: string | null;
  secondaryPlatform: string | null;
  secondaryTactic: string | null;
  secondaryName: string | null;
  spendOrEffect: 'spend' | 'effect';
  categoryType: 'direct' | 'added' | 'reduced';
  value: number;
}

export interface AnalyzeScenarioResult {
  results: LambdaResultSet<AnalyzeResultRow>;
  pairwise?: LambdaResultSet<RMMMPairwiseRow>;
  // Surfaced by the scenario-planning lambdas; drives dollars-vs-conversions in the UI.
  kpiType?: KpiType;
  outcomeUnit?: OutcomeUnit;
  cometErrorMessage?: string;
  cometProgress?: CometProgress;
  warnings?: HROASInstanceWarning[];
  // HROAS only
  modelStatus?: HroasModelStatus[];
  planNotes?: HroasPlanNote[];
}

// getBaselineBrandEquityScenario moved off the shared comet-py dispatcher onto its own
// self-invoke route (scenarioplanning-py app), same as getGeoMMMBaselineScenario above.
export const getBrandEquityBaselineScenario = (
  lambdaArgs: {
    stageOneModels: BrandEquityStageOneModelConfig[];
    stageTwoModelS3Key: string;
  },
  onProgress?: (progress: CometProgress) => void
): Promise<BaselineData> =>
  fetchCometSelfInvoke(
    ScenarioPlanningPyLambdaFetch,
    '/getBaselineBrandEquityScenario',
    lambdaArgs,
    { noTimeout: true, period: 3000, onProgress }
  );

// getBaselineGeoMMMScenario was the first of these 8 scenario-planning lambdas moved off the
// shared comet-py dispatcher onto its own self-invoke route (scenarioplanning-py app) -- all the
// others below have since followed the same pattern.
export const getGeoMMMBaselineScenario = (
  modelS3Key: string,
  onProgress?: (progress: CometProgress) => void
): Promise<BaselineData> =>
  fetchCometSelfInvoke(
    ScenarioPlanningPyLambdaFetch,
    '/getBaselineGeoMMMScenario',
    { modelS3Key },
    { noTimeout: true, period: 3000, onProgress }
  );

// getBaselineRMMMScenario moved off the shared comet-py dispatcher onto its own self-invoke
// route (scenarioplanning-py app), same as getGeoMMMBaselineScenario above.
export const getRMMMBaselineScenario = (
  lambdaArgs: {
    fxzModelS3Key: string;
    configS3Key: string;
    clientDataS3Key: string;
    gxWorkspaceS3Key: string;
    gxTracesS3Keys: string[];
  },
  onProgress?: (progress: CometProgress) => void
): Promise<BaselineData> =>
  fetchCometSelfInvoke(ScenarioPlanningPyLambdaFetch, '/getBaselineRMMMScenario', lambdaArgs, {
    noTimeout: true,
    period: 3000,
    onProgress
  });

// getBaselineHROASScenario moved off the shared comet-py dispatcher onto its own self-invoke
// route (scenarioplanning-py app), same as getGeoMMMBaselineScenario above.
export const getHROASBaselineScenarioLambda = (
  lambdaArgs: HROASRunArgs,
  onProgress?: (progress: CometProgress) => void
): Promise<BaselineData> =>
  fetchCometSelfInvoke(ScenarioPlanningPyLambdaFetch, '/getBaselineHROASScenario', lambdaArgs, {
    noTimeout: true,
    period: 3000,
    onProgress
  });

// analyzeBrandEquityScenario moved off the shared comet-py dispatcher onto its own self-invoke
// route (scenarioplanning-py app), same as getGeoMMMBaselineScenario above.
export const analyzeBrandEquityScenarioLambda = (
  lambdaArgs: {
    stageOneModels: BrandEquityStageOneModelConfig[];
    stageTwoModelS3Key: string;
    newScenario: ScenarioLambdaInput;
  },
  onProgress?: (progress: CometProgress) => void
): Promise<AnalyzeScenarioResult> =>
  fetchCometSelfInvoke(ScenarioPlanningPyLambdaFetch, '/analyzeBrandEquityScenario', lambdaArgs, {
    noTimeout: true,
    period: 3000,
    onProgress
  });

// analyzeGeoMMMScenario moved off the shared comet-py dispatcher onto its own self-invoke route
// (scenarioplanning-py app), same as getGeoMMMBaselineScenario above.
export const analyzeGeoMMMScenarioLambda = (
  lambdaArgs: {
    modelS3Key: string;
    newScenario: ScenarioLambdaInput;
  },
  onProgress?: (progress: CometProgress) => void
): Promise<AnalyzeScenarioResult> =>
  fetchCometSelfInvoke(ScenarioPlanningPyLambdaFetch, '/analyzeGeoMMMScenario', lambdaArgs, {
    noTimeout: true,
    period: 3000,
    onProgress
  });

// analyzeRMMMScenario moved off the shared comet-py dispatcher onto its own self-invoke route
// (scenarioplanning-py app), same as getGeoMMMBaselineScenario above.
export const analyzeRMMMScenarioLambda = (
  lambdaArgs: {
    fxzModelS3Key: string;
    configS3Key: string;
    clientDataS3Key: string;
    gxWorkspaceS3Key: string;
    gxTracesS3Keys: string[];
    newScenario: ScenarioLambdaInput;
  },
  onProgress?: (progress: CometProgress) => void
): Promise<AnalyzeScenarioResult> =>
  fetchCometSelfInvoke(ScenarioPlanningPyLambdaFetch, '/analyzeRMMMScenario', lambdaArgs, {
    noTimeout: true,
    period: 3000,
    onProgress
  });

// analyzeHROASScenario moved off the shared comet-py dispatcher onto its own self-invoke route
// (scenarioplanning-py app), same as getGeoMMMBaselineScenario above.
export const analyzeHROASScenarioLambda = (
  lambdaArgs: HROASRunArgs & {
    newScenario: ScenarioLambdaInput;
  },
  onProgress?: (progress: CometProgress) => void
): Promise<AnalyzeScenarioResult> =>
  fetchCometSelfInvoke(ScenarioPlanningPyLambdaFetch, '/analyzeHROASScenario', lambdaArgs, {
    noTimeout: true,
    period: 3000,
    onProgress
  });
